import { createContext, useContext, useEffect, useState, useCallback, useRef, ReactNode } from 'react'
import { Socket, Channel } from 'phoenix'
import { useAuth } from './AuthContext'

export interface NotificationPayload {
  id: string
  type: string
  title: string
  message: string
  reference_id?: string
  created_at: string
  read: boolean
}

export interface ChatMessagePayload {
  id: string
  content: string
  sender_id: string
  sender_name: string
  sender_avatar_url?: string
  sender_department?: string
  sender_cargo?: string
  sender_company_type: 'shipper' | 'freight-forwarder'
  attachment?: {
    key: string
    url: string
    name: string
    mime_type: string
    size: number
  }
  created_at: string
}

export interface MessagesReadPayload {
  reader_company_type: 'shipper' | 'freight-forwarder'
}

export interface TypingPayload {
  user_id: string
  is_typing: boolean
}

export interface MessageDeletedPayload {
  message_id: string
}

interface OrderChannelCallbacks {
  onNewMessage?: (message: ChatMessagePayload) => void
  onMessagesRead?: (payload: MessagesReadPayload) => void
  onTyping?: (payload: TypingPayload) => void
  onMessageDeleted?: (payload: MessageDeletedPayload) => void
}

interface WebSocketContextType {
  isConnected: boolean
  onNotification: (callback: (notification: NotificationPayload) => void) => () => void
  sendPing: () => void
  subscribeToOrder: (orderId: string, callbacks: OrderChannelCallbacks) => () => void
  sendTypingIndicator: (orderId: string, isTyping: boolean) => void
}

const WebSocketContext = createContext<WebSocketContextType | null>(null)

const PHOENIX_WS_URL = import.meta.env.VITE_REALTIME_URL || 'ws://localhost:4001/socket'

export function WebSocketProvider({ children }: { children: ReactNode }) {
  const { user, isAuthenticated } = useAuth()
  const [isConnected, setIsConnected] = useState(false)
  const socketRef = useRef<Socket | null>(null)
  const channelRef = useRef<Channel | null>(null)
  const orderChannelsRef = useRef<Map<string, Channel>>(new Map())
  const listenersRef = useRef<Set<(notification: NotificationPayload) => void>>(new Set())
  const reconnectAttempts = useRef(0)
  const maxReconnectAttempts = 5

  useEffect(() => {
    // Only connect if user is authenticated
    if (!isAuthenticated || !user) {
      // Clean up existing connection if user logs out
      if (socketRef.current) {
        channelRef.current?.leave()
        socketRef.current.disconnect()
        socketRef.current = null
        channelRef.current = null
        setIsConnected(false)
      }
      return
    }

    // Get token from localStorage
    const token = localStorage.getItem('access_token')
    if (!token) {
      console.warn('No access token available for WebSocket connection')
      return
    }

    // Create socket connection
    const socket = new Socket(PHOENIX_WS_URL, {
      params: { token },
      reconnectAfterMs: (tries) => {
        // Exponential backoff: 1s, 2s, 4s, 8s, 16s, then cap at 30s
        return Math.min(1000 * Math.pow(2, tries - 1), 30000)
      },
      heartbeatIntervalMs: 30000,
    })

    socket.onOpen(() => {
      console.log('[WebSocket] Connected to Phoenix')
      setIsConnected(true)
      reconnectAttempts.current = 0
    })

    socket.onClose((event) => {
      console.log('[WebSocket] Disconnected from Phoenix', event)
      setIsConnected(false)
    })

    socket.onError((error) => {
      console.warn('[WebSocket] Connection error:', error)
      reconnectAttempts.current++

      if (reconnectAttempts.current >= maxReconnectAttempts) {
        console.warn('[WebSocket] Max reconnect attempts reached, falling back to polling')
      }
    })

    socket.connect()
    socketRef.current = socket

    // Join user-specific channel
    const channel = socket.channel(`user:${user.id}`, {})

    channel.on('new_notification', (notification: NotificationPayload) => {
      console.log('[WebSocket] New notification received:', notification)
      // Notify all listeners
      listenersRef.current.forEach(callback => {
        try {
          callback(notification)
        } catch (err) {
          console.error('[WebSocket] Error in notification listener:', err)
        }
      })
    })

    channel.join()
      .receive('ok', () => {
        console.log(`[WebSocket] Joined user channel: user:${user.id}`)
      })
      .receive('error', (err) => {
        console.error('[WebSocket] Failed to join channel:', err)
      })
      .receive('timeout', () => {
        console.warn('[WebSocket] Channel join timeout')
      })

    channelRef.current = channel

    // Cleanup on unmount or user change
    return () => {
      console.log('[WebSocket] Cleaning up connection')
      channel.leave()
      socket.disconnect()
      socketRef.current = null
      channelRef.current = null
      setIsConnected(false)
    }
  }, [user?.id, isAuthenticated])

  // Register a notification listener
  const onNotification = useCallback((callback: (notification: NotificationPayload) => void) => {
    listenersRef.current.add(callback)

    // Return unsubscribe function
    return () => {
      listenersRef.current.delete(callback)
    }
  }, [])

  // Send a ping to check connection
  const sendPing = useCallback(() => {
    if (channelRef.current) {
      channelRef.current.push('ping', {})
        .receive('ok', (resp) => {
          console.log('[WebSocket] Pong received:', resp)
        })
        .receive('error', (err) => {
          console.error('[WebSocket] Ping failed:', err)
        })
    }
  }, [])

  // Subscribe to an order channel for real-time chat
  const subscribeToOrder = useCallback((orderId: string, callbacks: OrderChannelCallbacks) => {
    if (!socketRef.current) {
      console.warn('[WebSocket] Cannot subscribe to order - socket not connected')
      return () => {}
    }

    // Check if already subscribed
    let channel = orderChannelsRef.current.get(orderId)

    if (!channel) {
      // Create new channel
      channel = socketRef.current.channel(`order:${orderId}`, {})

      channel.on('new_message', (message: ChatMessagePayload) => {
        console.log('[WebSocket] New chat message received:', message)
        callbacks.onNewMessage?.(message)
      })

      channel.on('message_read', (payload: MessagesReadPayload) => {
        console.log('[WebSocket] Messages read:', payload)
        callbacks.onMessagesRead?.(payload)
      })

      channel.on('typing', (payload: TypingPayload) => {
        callbacks.onTyping?.(payload)
      })

      channel.on('message_deleted', (payload: MessageDeletedPayload) => {
        console.log('[WebSocket] Message deleted:', payload)
        callbacks.onMessageDeleted?.(payload)
      })

      channel.join()
        .receive('ok', () => {
          console.log(`[WebSocket] Joined order channel: order:${orderId}`)
        })
        .receive('error', (err) => {
          console.error('[WebSocket] Failed to join order channel:', err)
        })
        .receive('timeout', () => {
          console.warn('[WebSocket] Order channel join timeout')
        })

      orderChannelsRef.current.set(orderId, channel)
    }

    // Return unsubscribe function
    return () => {
      const ch = orderChannelsRef.current.get(orderId)
      if (ch) {
        console.log(`[WebSocket] Leaving order channel: order:${orderId}`)
        ch.leave()
        orderChannelsRef.current.delete(orderId)
      }
    }
  }, [])

  // Send typing indicator to an order channel
  const sendTypingIndicator = useCallback((orderId: string, isTyping: boolean) => {
    const channel = orderChannelsRef.current.get(orderId)
    if (channel) {
      channel.push('typing', { is_typing: isTyping })
    }
  }, [])

  const value: WebSocketContextType = {
    isConnected,
    onNotification,
    sendPing,
    subscribeToOrder,
    sendTypingIndicator,
  }

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  )
}

export function useWebSocket() {
  const context = useContext(WebSocketContext)
  if (!context) {
    throw new Error('useWebSocket must be used within WebSocketProvider')
  }
  return context
}

// Optional hook that doesn't throw if provider is missing
// Useful for components that may be used outside the provider
export function useWebSocketOptional() {
  return useContext(WebSocketContext)
}
